<script setup>
import { computed } from 'vue';
import { usePage, Link, router } from '@inertiajs/vue3';
import { Wallet, User, LogOut, Home, Receipt, UserCheck, Shield } from '@lucide/vue';
import AppLogo from '@/Components/AppLogo.vue';
import GuestBanner from '@/Components/GuestBanner.vue';

const page = usePage();
const auth = computed(() => page.props.auth);
const user = computed(() => auth.value?.user);
const isGuest = computed(() => auth.value?.is_guest);
const canAccessBackoffice = computed(() => auth.value?.can_access_backoffice);
const currentRoute = computed(() => page.url.split('?')[0]);
const navClass = path => currentRoute.value === path
  ? 'text-indigo-600 bg-indigo-50 font-bold'
  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium';
const mobileNavClass = path => currentRoute.value === path ? 'text-indigo-600 font-bold' : 'text-slate-500';

function formatRupiah(minor) {
  if (minor === null || minor === undefined) return 'Rp 0';
  return 'Rp ' + Number(minor).toLocaleString('id-ID');
}

function exitGuestMode() {
  router.post(route('guest.logout'));
}
</script>

<template>
  <div class="min-h-screen bg-slate-50 text-slate-900 flex flex-col justify-between select-none pb-20 sm:pb-0">
    <!-- Top Sticky Area -->
    <div class="sticky top-0 z-50">
      <GuestBanner v-if="isGuest" />

      <!-- Main Header Bar -->
      <header class="bg-white border-b border-slate-200/80 shadow-xs">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link :href="route('dashboard')" class="flex items-center">
            <AppLogo size="sm" light />
          </Link>

          <!-- Right Widgets / Auth Control -->
          <div class="flex items-center gap-3">
            <!-- Registered Wallet Widget -->
            <div v-if="user?.wallet" class="hidden sm:flex items-center gap-2 bg-slate-50 border border-slate-200/90 px-3 py-1.5 rounded-xl text-xs">
              <Wallet class="w-4 h-4 text-emerald-600" />
              <div class="flex flex-col">
                <span class="text-[10px] text-slate-500 font-medium leading-none">Saldo Utama</span>
                <span class="font-bold text-emerald-600 leading-tight mt-0.5">{{ formatRupiah(user.wallet.available_minor) }}</span>
              </div>
            </div>

            <!-- Guest Mode Widget -->
            <div v-else-if="isGuest" class="hidden sm:flex items-center gap-2 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-xl text-xs">
              <UserCheck class="w-4 h-4 text-amber-600" />
              <div class="flex flex-col">
                <span class="text-[10px] text-amber-700 font-medium leading-none">Saldo Dompet</span>
                <span class="font-bold text-amber-800 leading-tight mt-0.5">Saldo tersedia setelah login</span>
              </div>
            </div>

            <!-- Desktop Nav Links -->
            <nav class="hidden md:flex items-center gap-1 ml-4 border-l border-slate-200 pl-4">
              <Link :href="route('dashboard')" :class="navClass('/dashboard')" class="px-3 py-2 rounded-lg text-xs">
                Beranda
              </Link>
              <Link :href="route('transactions.index')" :class="navClass('/transactions')" class="px-3 py-2 rounded-lg text-xs">Transaksi</Link>
              <Link v-if="user" :href="route('settings')" :class="navClass('/settings')" class="px-3 py-2 rounded-lg text-xs">
                Pengaturan
              </Link>
            </nav>

            <!-- User Menu / Exit -->
            <div class="flex items-center gap-2">
              <template v-if="user">
                <!-- Back-Office Quick Navigation Action -->
                <Link
                  v-if="canAccessBackoffice"
                  :href="route('admin.dashboard')"
                  class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-bold hover:bg-indigo-100 transition shadow-2xs"
                >
                  <Shield class="w-3.5 h-3.5 text-indigo-600" />
                  <span>Buka Back Office</span>
                </Link>

                <Link :href="route('settings')" class="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-800 transition">
                  <User class="w-3.5 h-3.5" />
                  <span>{{ user.display_name }}</span>
                </Link>
                <Link :href="route('logout')" method="post" as="button" class="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition">
                  <LogOut class="w-4 h-4" />
                </Link>
              </template>

              <template v-else-if="isGuest">
                <button
                  @click="exitGuestMode"
                  type="button"
                  class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700 transition"
                >
                  <LogOut class="w-3.5 h-3.5 text-amber-600" />
                  <span class="hidden sm:inline">Keluar Mode Tamu</span>
                </button>
              </template>
            </div>
          </div>
        </div>
      </header>
    </div>

    <!-- Main Content Body -->
    <main class="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <slot />
    </main>

    <!-- Mobile Bottom Navigation Bar -->
    <div class="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-40 md:hidden shadow-lg">
      <div class="grid grid-cols-3 h-16">
        <Link :href="route('dashboard')" :class="mobileNavClass('/dashboard')" class="flex flex-col items-center justify-center">
          <Home class="w-5 h-5" />
          <span class="text-[10px] mt-1">Beranda</span>
        </Link>

        <Link :href="route('transactions.index')" :class="mobileNavClass('/transactions')" class="flex flex-col items-center justify-center">
          <Receipt class="w-5 h-5" />
          <span class="text-[10px] mt-1">Transaksi</span>
        </Link>

        <Link v-if="user" :href="route('settings')" :class="mobileNavClass('/settings')" class="flex flex-col items-center justify-center">
          <User class="w-5 h-5" />
          <span class="text-[10px] mt-1">Akun</span>
        </Link>
        <div v-else @click="exitGuestMode" class="flex flex-col items-center justify-center text-amber-600 cursor-pointer">
          <UserCheck class="w-5 h-5" />
          <span class="text-[10px] mt-1">Tamu</span>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="bg-white border-t border-slate-200/80 py-6 text-center text-xs text-slate-500">
      <div class="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2">
        <div>&copy; 2026 Manza PPOB. Platform Multi-Produk Digital (Pelanggan & Reseller).</div>
        <div class="text-[11px] text-slate-400">Pusat Bantuan & Support</div>
      </div>
    </footer>
  </div>
</template>
